import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import styles from './style.module.scss'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

function TextAreaField(props) {
	const { t } = useTranslation()
	const [count, setCount] = useState(props.value ? props.value.length : 0)
	// Wie viele Zeichen beim letzten Einfuegen verloren gingen. `maxLength` am
	// <textarea> schneidet stillschweigend ab — wer einen fertigen Text
	// hineinkopiert, merkt sonst nicht, dass das Ende fehlt.
	const [cutOff, setCutOff] = useState(0)
	const minCharacters = 100

	useEffect(() => {
		setCount(props.value ? props.value.length : 0)
	}, [props.value])

	const left = props.maxLength ? Math.max(props.maxLength - count, 0) : 0
	const isFull = props.maxLength ? count >= props.maxLength : false
	/*
	 * Ein bereits gespeicherter Text kann laenger sein als die Grenze —
	 * `maxLength` am <textarea> haelt nur neue Eingaben auf, es kuerzt nichts
	 * Vorhandenes. Auf der Testumgebung liegt die Geschaeftsidee bei rund 60 %
	 * der Firmen ueber 200 Zeichen (Stichprobe 08.08.2026). „noch 0 von 200"
	 * waere dort falsch: Es fehlt nicht nichts, es ist zu viel — und das
	 * Speichern scheitert, solange nicht gekuerzt wird.
	 */
	const over = props.maxLength ? Math.max(count - props.maxLength, 0) : 0

	/*
	 * Beim Einfuegen vorher rechnen: Der Platz ist die Hoechstlaenge minus dem,
	 * was stehen bleibt (die Auswahl wird ja ersetzt). Passt der eingefuegte
	 * Text nicht hinein, steht die Zahl der abgeschnittenen Zeichen unter dem
	 * Feld — bis zur naechsten Eingabe.
	 */
	const handlePaste = event => {
		if (!props.maxLength) return

		const field = event.target
		const pasted = (event.clipboardData || window.clipboardData)?.getData('text') || ''
		const selected = (field.selectionEnd || 0) - (field.selectionStart || 0)
		const room = props.maxLength - (field.value.length - selected)

		setCutOff(pasted.length > room ? pasted.length - room : 0)
	}

	return (
		<label style={props.mb ? { marginBottom: props.mb } : {}} className={clsx(styles.root, props.classNameLabel)}>
			<div className={styles.fieldHeader}>
				{props.label && (
					<span className={styles.inputName}>
						{props.label} {props.required ? <span className={styles.require}>*</span> : ''}
					</span>
				)}
				{props.showError && (
					<p className={styles.inputWarning}>
						<span
							className={clsx({
								[styles.default]: count > minCharacters,
								[styles.errorColor]: count <= minCharacters
							})}
						>
							Min 100 {t('Characters')}
						</span>
					</p>
				)}
				{/* Die Grenze steht immer da, der Rest zaehlt beim Tippen mit. */}
				{props.showLettersLeft && props.maxLength && (
					<p className={styles.inputWarning}>
						<span className={clsx(styles.counter, isFull && styles.counterFull)}>
							{over
								? t('Characters over limit', { over, max: props.maxLength })
								: t('Characters left', { left, max: props.maxLength })}
						</span>
					</p>
				)}
			</div>
			{props.hint && <span className={styles.hint}>{props.hint}</span>}
			<textarea
				maxLength={props.maxLength}
				disabled={props.disabled || props.isTeacherStudentView}
				className={styles.input}
				style={{
					height: props.height ? `${props.height}px` : '',
					resize: count > 150 ? 'vertical' : 'none'
				}}
				{...props.register}
				onPaste={handlePaste}
				onChange={e => {
					props.register.onChange(e)
					setCount(e.target.value.length)
					if (cutOff) setCutOff(0)
				}}
				placeholder={props.placeholder}
				name={props.register.name}
			/>
			{Boolean(cutOff) && (
				<span className={styles.cutOff}>
					{t('Pasted text shortened', { cut: cutOff, max: props.maxLength })}
				</span>
			)}
			{props.error && <span className={styles.error}>{props.error.message}</span>}
		</label>
	)
}

TextAreaField.propTypes = {
	label: PropTypes.string.isRequired,
	error: PropTypes.object,
	register: PropTypes.object.isRequired,
	placeholder: PropTypes.string.isRequired,
	height: PropTypes.number,
	showLettersLeft: PropTypes.bool,
	showError: PropTypes.bool,
	maxLength: PropTypes.number,
	required: PropTypes.bool,
	value: PropTypes.string,
	hint: PropTypes.string,

	mb: PropTypes.number,

	disabled: PropTypes.bool,

	classNameLabel: PropTypes.string,
	isTeacherStudentView: PropTypes.bool.isRequired
}

TextAreaField.defaultProps = {
	disabled: false
}

const mapStateToProps = state => ({
	isTeacherStudentView: state.user.isTeacherStudentView
})

export default connect(mapStateToProps)(TextAreaField)
